import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SMSLayout from '../components/SMSLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MessageSquare,
  Upload,
  FileSpreadsheet,
  Phone,
  Send,
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle,
  X,
  Loader2,
  Smartphone,
  Info,
  RefreshCw,
  Zap,
  Check,
  Database,
  Eye,
  FileText,
  Megaphone,
  Bell,
  Variable
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// GSM-7 character set for proper encoding calculation
const GSM7_CHARS = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXTENDED = '^{}\\[~]|€';

// Days of week for weekly recurrence
const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Lun' },
  { value: 'tuesday', label: 'Mar' },
  { value: 'wednesday', label: 'Mer' },
  { value: 'thursday', label: 'Jeu' },
  { value: 'friday', label: 'Ven' },
  { value: 'saturday', label: 'Sam' },
  { value: 'sunday', label: 'Dim' },
];

const SMSMailingNew = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // === SMS Config from SUPADMIN ===
  const [smsConfig, setSmsConfig] = useState({
    sender_id: '',
    custom_message: '',
    show_sender_id: false
  });
  const [configLoading, setConfigLoading] = useState(true);
  
  // === STEP 1: Campaign Info ===
  const [campaignType, setCampaignType] = useState('marketing');
  const [campaignName, setCampaignName] = useState('');
  
  // === STEP 2: Message ===
  const [message, setMessage] = useState('');
  
  // === STEP 3: Data (Données) ===
  const [dataSource, setDataSource] = useState('upload');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContacts, setFileContacts] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [phoneColumn, setPhoneColumn] = useState('');
  const [uploading, setUploading] = useState(false);
  const [filePreview, setFilePreview] = useState([]);
  const [fileName, setFileName] = useState('');
  const [fileValidated, setFileValidated] = useState(false);
  const [savedFiles, setSavedFiles] = useState([]);
  const [selectedSavedFile, setSelectedSavedFile] = useState('');
  const [manualNumbers, setManualNumbers] = useState('');
  
  // === Column Mapping for Variables ===
  const [columnMapping, setColumnMapping] = useState({});
  const [showPersonalizationPreview, setShowPersonalizationPreview] = useState(false);
  const [personalizationPreviews, setPersonalizationPreviews] = useState([]);
  
  // Available variables for mapping
  const AVAILABLE_VARIABLES = [
    { key: 'prenom', label: 'Prénom' },
    { key: 'nom', label: 'Nom' },
    { key: 'email', label: 'Email' },
    { key: 'societe', label: 'Société' },
    { key: 'code', label: 'Code' },
    { key: 'lien', label: 'Lien' },
    { key: 'montant', label: 'Montant' },
    { key: 'ref', label: 'Référence' }
  ];
  
  // Check if message has variables
  const hasVariables = (text) => /\{(\w+)\}/.test(text);
  const messageHasVariables = hasVariables(message);
  
  // === STEP 4: Scheduling & Send ===
  const [sendMode, setSendMode] = useState('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [recurringPattern, setRecurringPattern] = useState('daily');
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [recurringDays, setRecurringDays] = useState(['monday']);
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringStartTime, setRecurringStartTime] = useState('09:00');
  const [recurringEndType, setRecurringEndType] = useState('never');
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringOccurrences, setRecurringOccurrences] = useState(10);
  
  // === TEST SMS ===
  const [testNumbers, setTestNumbers] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [testValidated, setTestValidated] = useState(false);
  
  // === SEND ===
  const [sending, setSending] = useState(false);

  // Load SMS config from SUPADMIN settings
  useEffect(() => {
    const loadSmsConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/v2/admin/sms-config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSmsConfig({
          sender_id: response.data?.sender_id || '',
          custom_message: response.data?.custom_message || '',
          show_sender_id: !!response.data?.sender_id
        });
      } catch (error) {
        console.log('No SMS config found, using defaults');
      } finally {
        setConfigLoading(false);
      }
    };
    
    const loadSavedFiles = async () => {
      try {
        const response = await axios.get(`${API_URL}/sms-mailing/saved-files`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedFiles(response.data || []);
      } catch (error) {
        console.log('No saved files');
      }
    };
    
    loadSmsConfig();
    loadSavedFiles();
    
    const today = new Date().toISOString().split('T')[0];
    setScheduledDate(today);
    setRecurringStartDate(today);
  }, [token]);

  // Calculate SMS metrics with proper concatenation rules
  const calculateSMSMetrics = useCallback((text) => {
    if (!text) return { chars: 0, smsCount: 1, encoding: 'GSM-7', maxChars: 612, fullText: '' };
    
    let fullText = text;
    // Add stopsms.io with 2 line breaks for marketing
    if (campaignType === 'marketing') {
      fullText += '\n\nstopsms.io';
    }
    
    let isGSM7 = true;
    let extendedCount = 0;
    
    for (const char of fullText) {
      if (GSM7_EXTENDED.includes(char)) {
        extendedCount++;
      } else if (!GSM7_CHARS.includes(char)) {
        isGSM7 = false;
        break;
      }
    }
    
    const encoding = isGSM7 ? 'GSM-7' : 'Unicode';
    const charCount = isGSM7 ? fullText.length + extendedCount : fullText.length;
    
    let smsCount, maxChars;
    if (isGSM7) {
      // GSM-7: 160 chars for single SMS, 153 chars per segment for concatenated
      maxChars = 612; // 4 SMS max (4 * 153 = 612)
      if (charCount <= 160) {
        smsCount = 1;
      } else {
        smsCount = Math.ceil(charCount / 153);
      }
    } else {
      // Unicode: 70 chars for single SMS, 67 chars per segment for concatenated
      maxChars = 268; // 4 SMS max (4 * 67 = 268)
      if (charCount <= 70) {
        smsCount = 1;
      } else {
        smsCount = Math.ceil(charCount / 67);
      }
    }
    
    return { chars: charCount, smsCount, encoding, maxChars, fullText };
  }, [campaignType]);

  const smsMetrics = calculateSMSMetrics(message);

  // Sanitize filename (remove accents and special characters)
  const sanitizeFileName = (name) => {
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 50);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setFileValidated(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/sms-mailing/parse-file`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const baseName = file.name.replace(/\.(csv|xlsx|xls)$/i, '');
      setFileName(sanitizeFileName(baseName));
      setUploadedFile({ name: file.name, size: file.size });
      setFileContacts(response.data.contacts || []);
      setFileColumns(response.data.columns || []);
      setFilePreview((response.data.contacts || []).slice(0, 5));
      
      // Auto-detect phone column
      const phoneColNames = ['mobile', 'tel', 'telephone', 'phone', 'numero', 'numéro', 'gsm', 'portable'];
      const detectedCol = response.data.columns.find(col => 
        phoneColNames.includes(col.toLowerCase())
      );
      if (detectedCol) setPhoneColumn(detectedCol);
      
      toast.success(`${response.data.contacts?.length || 0} lignes chargées - Vérifiez les 5 premières lignes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setUploading(false);
    }
  };

  // Validate and import file
  const handleValidateFile = async () => {
    if (!phoneColumn) {
      toast.error('Sélectionnez la colonne téléphone');
      return;
    }
    
    try {
      // Save file reference
      await axios.post(`${API_URL}/sms-mailing/save-file`, {
        name: fileName,
        columns: fileColumns,
        contacts_count: fileContacts.length,
        phone_column: phoneColumn
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFileValidated(true);
      toast.success(`${fileContacts.length} contacts importés avec succès`);
      
      // Refresh saved files list
      const response = await axios.get(`${API_URL}/sms-mailing/saved-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedFiles(response.data || []);
    } catch (error) {
      // Even if save fails, allow to continue
      setFileValidated(true);
      toast.success(`${fileContacts.length} contacts prêts`);
    }
  };

  // Cancel file upload
  const handleCancelFile = () => {
    setUploadedFile(null);
    setFileContacts([]);
    setFileColumns([]);
    setFilePreview([]);
    setFileName('');
    setPhoneColumn('');
    setFileValidated(false);
  };

  // Load saved file
  const handleLoadSavedFile = async (fileId) => {
    try {
      const response = await axios.get(`${API_URL}/sms-mailing/saved-files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedSavedFile(fileId);
      setFileContacts(response.data.contacts || []);
      setFileColumns(response.data.columns || []);
      setPhoneColumn(response.data.phone_column || '');
      setFileValidated(true);
      toast.success(`Fichier "${response.data.name}" chargé`);
    } catch (error) {
      toast.error('Erreur lors du chargement du fichier');
    }
  };

  // Insert variable into message
  const insertVariable = (varName) => {
    setMessage(prev => prev + `{${varName}}`);
  };

  // Get recipients count
  const getRecipientsCount = () => {
    if (dataSource === 'upload' || dataSource === 'saved') {
      return fileContacts.length;
    }
    return manualNumbers.split(/[\n,;]/).filter(n => n.trim()).length;
  };

  // Send test SMS
  const handleSendTest = async () => {
    const numbers = testNumbers.split(/[\n,;]/).filter(n => n.trim());
    if (numbers.length === 0) {
      toast.error('Entrez au moins un numéro de test');
      return;
    }
    if (!message) {
      toast.error('Entrez un message');
      return;
    }
    
    setTestSending(true);
    setTestResults([]);
    
    const results = [];
    
    for (const phone of numbers) {
      try {
        await axios.post(`${API_URL}/sms-mailing/send-test`, {
          phone: phone.trim(),
          message: smsMetrics.fullText,
          sender_id: smsConfig.sender_id || 'SMS'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        results.push({ phone: phone.trim(), status: 'success' });
      } catch (error) {
        results.push({ phone: phone.trim(), status: 'error', error: error.response?.data?.detail });
      }
    }
    
    setTestResults(results);
    setTestSending(false);
    
    const successCount = results.filter(r => r.status === 'success').length;
    if (successCount === results.length) {
      toast.success(`${successCount} SMS de test envoyé(s) avec succès !`);
    } else {
      toast.warning(`${successCount}/${results.length} SMS envoyé(s)`);
    }
  };

  // Validate test
  const handleValidateTest = () => {
    setTestValidated(true);
    toast.success('Test validé ! Vous pouvez maintenant envoyer la campagne.');
  };

  // Send campaign
  const handleSendCampaign = async () => {
    if (!campaignName) {
      toast.error('Donnez un nom à votre campagne');
      return;
    }
    if (!message) {
      toast.error('Entrez un message');
      return;
    }
    if (getRecipientsCount() === 0) {
      toast.error('Ajoutez des destinataires');
      return;
    }
    
    setSending(true);
    try {
      // Build recipients list
      let recipients = [];
      let contacts = null;
      
      if (dataSource === 'upload' || dataSource === 'saved') {
        // Use file contacts
        recipients = fileContacts.map(c => c[phoneColumn]);
        
        // If message has variables, build rich contacts with mapped data
        if (messageHasVariables && Object.keys(columnMapping).length > 0) {
          contacts = fileContacts.map(contact => {
            const enrichedContact = {
              phone: contact[phoneColumn]
            };
            
            // Map columns to variables
            Object.entries(columnMapping).forEach(([variable, column]) => {
              if (column && contact[column]) {
                enrichedContact[variable] = contact[column];
              }
            });
            
            return enrichedContact;
          });
        }
      } else {
        // Manual entry
        recipients = manualNumbers.split(/[\n,;]/).filter(n => n.trim());
      }
      
      const payload = {
        name: campaignName,
        type: campaignType,
        message: smsMetrics.fullText,
        sender_id: smsConfig.sender_id || 'SMS',
        recipients,
        contacts: contacts,  // Rich contacts for personalization
        send_mode: sendMode,
      };
      
      if (sendMode === 'scheduled') {
        payload.scheduled_at = `${scheduledDate}T${scheduledTime}`;
      } else if (sendMode === 'recurring') {
        payload.recurring = {
          pattern: recurringPattern,
          interval: recurringInterval,
          days: recurringPattern === 'weekly' ? recurringDays : null,
          start_date: recurringStartDate,
          start_time: recurringStartTime,
          end_type: recurringEndType,
          end_date: recurringEndType === 'until' ? recurringEndDate : null,
          occurrences: recurringEndType === 'count' ? recurringOccurrences : null
        };
      }
      
      const response = await axios.post(`${API_URL}/sms-mailing/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show personalization info if applicable
      const isPersonalized = response.data?.personalized;
      
      if (sendMode === 'immediate') {
        toast.success(isPersonalized 
          ? 'Campagne personnalisée envoyée avec succès !' 
          : 'Campagne envoyée avec succès !');
        navigate('/sms/archives');
      } else {
        toast.success(isPersonalized 
          ? 'Campagne personnalisée programmée !' 
          : 'Campagne programmée avec succès !');
        navigate('/sms/queue');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  // Toggle day selection for weekly recurrence
  const toggleDay = (day) => {
    if (recurringDays.includes(day)) {
      if (recurringDays.length > 1) {
        setRecurringDays(recurringDays.filter(d => d !== day));
      }
    } else {
      setRecurringDays([...recurringDays, day]);
    }
  };

  // Preview personalization
  const handlePreviewPersonalization = async () => {
    if (!messageHasVariables || fileContacts.length === 0) return;
    
    // Build contacts with mapping
    const contacts = fileContacts.slice(0, 5).map(contact => {
      const enrichedContact = {
        phone: contact[phoneColumn]
      };
      Object.entries(columnMapping).forEach(([variable, column]) => {
        if (column && contact[column]) {
          enrichedContact[variable] = contact[column];
        }
      });
      return enrichedContact;
    });
    
    try {
      const response = await axios.post(`${API_URL}/sms-mailing/preview-personalization`, {
        message: smsMetrics.fullText,
        contacts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPersonalizationPreviews(response.data.previews || []);
      setShowPersonalizationPreview(true);
    } catch (error) {
      // Fallback: do client-side preview
      const previews = contacts.map(contact => {
        let personalized = smsMetrics.fullText;
        Object.entries(contact).forEach(([key, value]) => {
          personalized = personalized.replace(new RegExp(`\\{${key}\\}`, 'gi'), value || '');
        });
        // Replace date/time
        const now = new Date();
        personalized = personalized.replace(/\{date\}/gi, now.toLocaleDateString('fr-FR'));
        personalized = personalized.replace(/\{heure\}/gi, now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
        
        return {
          phone: contact.phone,
          personalized
        };
      });
      setPersonalizationPreviews(previews);
      setShowPersonalizationPreview(true);
    }
  };

  return (
    <SMSLayout>
      <div className="space-y-6" data-testid="sms-mailing-new-page">
        {/* Header */}
        <div>
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Nouvelle campagne SMS
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Créez, Envoyez votre campagne SMS en quelques clics
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* === STEP 1: Campaign Info === */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    1
                  </div>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                    Informations campagne
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Type Selection FIRST */}
                <div className="space-y-2">
                  <Label className={isDarkMode ? 'text-slate-200' : ''}>Type de campagne *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div 
                      onClick={() => setCampaignType('marketing')}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        campaignType === 'marketing'
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10'
                          : isDarkMode ? 'border-[hsl(220,14%,25%)] hover:border-[hsl(220,14%,35%)]' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Megaphone className={`h-5 w-5 ${campaignType === 'marketing' ? 'text-indigo-600' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                        <div>
                          <p className={`font-semibold ${isDarkMode && campaignType !== 'marketing' ? 'text-slate-200' : ''}`}>
                            Marketing
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                            Promotions, offres
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="mt-2 text-xs border-indigo-400 text-indigo-600">
                        + stopsms.io
                      </Badge>
                    </div>
                    
                    <div 
                      onClick={() => setCampaignType('transactional')}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        campaignType === 'transactional'
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                          : isDarkMode ? 'border-[hsl(220,14%,25%)] hover:border-[hsl(220,14%,35%)]' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Bell className={`h-5 w-5 ${campaignType === 'transactional' ? 'text-emerald-600' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                        <div>
                          <p className={`font-semibold ${isDarkMode && campaignType !== 'transactional' ? 'text-slate-200' : ''}`}>
                            Transactionnel
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                            Notifications
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="mt-2 text-xs border-emerald-400 text-emerald-600">
                        Sans STOP
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Campaign Name */}
                <div className="space-y-2">
                  <Label className={isDarkMode ? 'text-slate-200' : ''}>Nom de la campagne *</Label>
                  <Input
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="Ex: Promo Janvier 2026"
                    className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                  />
                </div>
              </CardContent>
            </Card>

            {/* === STEP 2: Data (Données) === */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                      isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      2
                    </div>
                    <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                      Données
                    </CardTitle>
                  </div>
                  {getRecipientsCount() > 0 && (
                    <Badge className="bg-indigo-600">{getRecipientsCount()} contacts</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={dataSource} onValueChange={(v) => { setDataSource(v); setFileValidated(false); }}>
                  <TabsList className="grid w-full grid-cols-3 mb-4">
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Importer
                    </TabsTrigger>
                    <TabsTrigger value="saved" className="flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Fichiers sauvegardés
                    </TabsTrigger>
                    <TabsTrigger value="manual" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Saisie manuelle
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Upload Tab */}
                  <TabsContent value="upload" className="space-y-4">
                    {!uploadedFile ? (
                      <div className={`border-2 border-dashed rounded-xl p-6 text-center ${isDarkMode ? 'border-[hsl(220,14%,25%)]' : 'border-slate-200'}`}>
                        <Upload className={`h-8 w-8 mx-auto mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                        <p className={`mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          Glissez votre fichier ou cliquez pour sélectionner
                        </p>
                        <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          Formats : CSV, XLS, XLSX
                        </p>
                        <input type="file" accept=".csv,.xls,.xlsx" onChange={handleFileUpload} className="hidden" id="file-upload" />
                        <Button asChild disabled={uploading}>
                          <label htmlFor="file-upload" className="cursor-pointer">
                            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                            {uploading ? 'Chargement...' : 'Sélectionner un fichier'}
                          </label>
                        </Button>
                      </div>
                    ) : !fileValidated ? (
                      <div className="space-y-4">
                        {/* File info */}
                        <div className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <div className="flex items-center gap-3">
                            <FileSpreadsheet className="h-6 w-6 text-emerald-500" />
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{uploadedFile.name}</p>
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {fileContacts.length} lignes détectées
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Preview 5 first rows */}
                        {filePreview.length > 0 && (
                          <div className="space-y-2">
                            <Label className={isDarkMode ? 'text-slate-200' : ''}>
                              <Eye className="h-4 w-4 inline mr-1" />
                              Aperçu (5 premières lignes)
                            </Label>
                            <div className="overflow-x-auto">
                              <table className={`w-full text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <thead>
                                  <tr className={isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-100'}>
                                    {fileColumns.map((col) => (
                                      <th key={col} className="px-3 py-2 text-left font-medium">{col}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {filePreview.map((row, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? '' : isDarkMode ? 'bg-[hsl(220,14%,16%)]' : 'bg-slate-50'}>
                                      {fileColumns.map((col) => (
                                        <td key={col} className="px-3 py-2">{row[col]}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {/* File name and phone column */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className={isDarkMode ? 'text-slate-200' : ''}>Nom du fichier</Label>
                            <Input
                              value={fileName}
                              onChange={(e) => setFileName(sanitizeFileName(e.target.value))}
                              placeholder="Nom pour sauvegarder"
                              className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className={isDarkMode ? 'text-slate-200' : ''}>Colonne téléphone *</Label>
                            <Select value={phoneColumn} onValueChange={setPhoneColumn}>
                              <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                                <SelectValue placeholder="Sélectionnez" />
                              </SelectTrigger>
                              <SelectContent>
                                {fileColumns.map((col) => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex gap-3">
                          <Button variant="outline" onClick={handleCancelFile}>
                            <X className="h-4 w-4 mr-2" />
                            Annuler
                          </Button>
                          <Button onClick={handleValidateFile} className="bg-emerald-600 hover:bg-emerald-700">
                            <Check className="h-4 w-4 mr-2" />
                            Valider et importer
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                          <div className="flex items-center gap-3">
                            <CheckCircle className="h-6 w-6 text-emerald-500" />
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                                {fileContacts.length} contacts importés
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-emerald-400/70' : 'text-emerald-600'}`}>
                                Colonne téléphone : {phoneColumn}
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={handleCancelFile}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Saved Files Tab */}
                  <TabsContent value="saved" className="space-y-4">
                    {savedFiles.length === 0 ? (
                      <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <Database className="h-10 w-10 mx-auto mb-3 opacity-50" />
                        <p>Aucun fichier sauvegardé</p>
                        <p className="text-sm">Importez d'abord un fichier dans l'onglet "Importer"</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {savedFiles.map((file) => (
                          <div 
                            key={file.id}
                            onClick={() => handleLoadSavedFile(file.id)}
                            className={`p-3 rounded-lg cursor-pointer transition-all ${
                              selectedSavedFile === file.id
                                ? isDarkMode ? 'bg-indigo-500/20 border border-indigo-500' : 'bg-indigo-50 border border-indigo-300'
                                : isDarkMode ? 'bg-[hsl(220,14%,18%)] hover:bg-[hsl(220,14%,22%)]' : 'bg-slate-50 hover:bg-slate-100'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileSpreadsheet className={`h-5 w-5 ${selectedSavedFile === file.id ? 'text-indigo-500' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                <div>
                                  <p className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{file.name}</p>
                                  <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {file.contacts_count} contacts
                                  </p>
                                </div>
                              </div>
                              {selectedSavedFile === file.id && (
                                <CheckCircle className="h-5 w-5 text-indigo-500" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Manual Entry Tab */}
                  <TabsContent value="manual">
                    <Textarea
                      value={manualNumbers}
                      onChange={(e) => setManualNumbers(e.target.value)}
                      placeholder="Un numéro par ligne&#10;&#10;0612345678&#10;0698765432"
                      rows={6}
                      className={`font-mono ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                    />
                  </TabsContent>
                </Tabs>
                
                {/* Summary integrated here */}
                {getRecipientsCount() > 0 && (
                  <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                    <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Résumé
                    </h4>
                    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      <div>
                        <p className="text-xs uppercase tracking-wide opacity-60">Type</p>
                        <p className="font-medium">{campaignType === 'marketing' ? 'Marketing' : 'Transactionnel'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide opacity-60">Destinataires</p>
                        <p className="font-medium">{getRecipientsCount()}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide opacity-60">SMS/message</p>
                        <p className="font-medium">{smsMetrics.smsCount}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide opacity-60">Total SMS</p>
                        <p className="font-bold text-lg text-indigo-600">{getRecipientsCount() * smsMetrics.smsCount}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* === STEP 3: Message === */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    3
                  </div>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                    Message
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left: Message Editor */}
                  <div className="space-y-3">
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tapez votre message ici...&#10;&#10;Utilisez {variable} pour personnaliser"
                      rows={8}
                      className={`font-mono ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                      data-testid="message-textarea"
                    />
                    
                    {/* Variables from file columns - shown when file is loaded */}
                    {fileColumns.length > 0 && (
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}>
                        <p className={`text-xs font-medium mb-2 flex items-center gap-1 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>
                          <Variable className="h-3 w-3" />
                          Variables disponibles (cliquez pour insérer) :
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {fileColumns.map((col) => (
                            <Badge 
                              key={col}
                              variant="secondary"
                              className="cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-800 text-xs"
                              onClick={() => insertVariable(col)}
                              data-testid={`variable-${col}`}
                            >
                              {`{${col}}`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Hint when no file is loaded */}
                    {fileColumns.length === 0 && (
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
                        <p className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                          <Variable className="h-3 w-3" />
                          Importez un fichier à l'étape 2 pour accéder aux variables de personnalisation
                        </p>
                      </div>
                    )}
                    
                    {/* Character count and SMS count */}
                    <div className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                      <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        <span className="font-medium">{smsMetrics.chars}</span>
                        <span className="text-slate-400"> / 612 caractères espaces compris</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={smsMetrics.smsCount <= 4 ? 'default' : 'destructive'} className="bg-indigo-600 text-xs">
                          {smsMetrics.smsCount} SMS
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Variable Mapping Section - when message has variables and file is loaded */}
                    {messageHasVariables && fileColumns.length > 0 && fileValidated && (
                      <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Variable className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                          <h4 className={`font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                            Personnalisation détectée
                          </h4>
                        </div>
                        <p className={`text-sm mb-4 ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>
                          Associez les colonnes de votre fichier aux variables du message.
                        </p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {AVAILABLE_VARIABLES.filter(v => 
                            message.toLowerCase().includes(`{${v.key}}`)
                          ).map((variable) => (
                            <div key={variable.key} className="flex items-center gap-2">
                              <span className={`text-sm font-mono w-24 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                {`{${variable.key}}`}
                              </span>
                              <span className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>→</span>
                              <Select 
                                value={columnMapping[variable.key] || ''} 
                                onValueChange={(value) => setColumnMapping({...columnMapping, [variable.key]: value})}
                              >
                                <SelectTrigger className={`flex-1 h-8 text-sm ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : ''}`}>
                                  <SelectValue placeholder="Colonne..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {fileColumns.map((col) => (
                                    <SelectItem key={col} value={col}>{col}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>
                        
                        {/* Preview Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePreviewPersonalization}
                          className={`mt-4 ${isDarkMode ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/20' : ''}`}
                          data-testid="preview-personalization-btn"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Prévisualiser la personnalisation
                        </Button>
                        
                        {/* Preview Results */}
                        {showPersonalizationPreview && personalizationPreviews.length > 0 && (
                          <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,12%)]' : 'bg-white'}`}>
                            <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Aperçu (premiers contacts) :
                            </p>
                            <div className="space-y-2">
                              {personalizationPreviews.map((preview, idx) => (
                                <div key={idx} className={`p-2 rounded text-sm ${isDarkMode ? 'bg-[hsl(220,14%,16%)]' : 'bg-slate-50'}`}>
                                  <span className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {preview.phone}:
                                  </span>
                                  <p className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                                    {preview.personalized}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Right: Mobile Preview */}
                  <div className="flex justify-center">
                    <div className="w-64 bg-slate-900 rounded-3xl p-3 shadow-2xl">
                      {/* Phone notch */}
                      <div className="flex justify-center mb-2">
                        <div className="w-20 h-5 bg-black rounded-full"></div>
                      </div>
                      <div className="bg-white rounded-2xl p-3 min-h-[280px]">
                        {/* Message header */}
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <MessageSquare className="h-4 w-4 text-indigo-600" />
                          </div>
                          <span className="font-medium text-sm text-slate-900">
                            {smsConfig.sender_id || 'SMS'}
                          </span>
                        </div>
                        {/* Message content */}
                        <div className="space-y-2">
                          <div className="bg-slate-100 rounded-xl p-3 text-sm text-slate-800">
                            {smsMetrics.fullText || (
                              <span className="text-slate-400 italic">Votre message apparaîtra ici</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 text-right">
                            {smsMetrics.smsCount} SMS • {smsMetrics.encoding}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* === STEP 4: Scheduling + Test + Send === */}
            <Card className={`border-2 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-indigo-500/30' : 'border-indigo-200'}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    4
                  </div>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                    Envoi
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Send Mode Tabs */}
                <Tabs value={sendMode} onValueChange={(v) => { setSendMode(v); setTestValidated(false); }}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="immediate" className="flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      Immédiat
                    </TabsTrigger>
                    <TabsTrigger value="scheduled" className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Différé
                    </TabsTrigger>
                    <TabsTrigger value="recurring" className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Récurrent
                    </TabsTrigger>
                  </TabsList>

                  {/* Immediate */}
                  <TabsContent value="immediate" className="mt-4">
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        <Zap className="h-4 w-4 inline mr-2" />
                        Le message sera envoyé immédiatement après validation.
                      </p>
                    </div>
                  </TabsContent>

                  {/* Scheduled */}
                  <TabsContent value="scheduled" className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-200' : ''}>Date d'envoi</Label>
                        <Input
                          type="date"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-200' : ''}>Heure</Label>
                        <Input
                          type="time"
                          value={scheduledTime}
                          onChange={(e) => setScheduledTime(e.target.value)}
                          className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Recurring */}
                  <TabsContent value="recurring" className="mt-4 space-y-4">
                    {/* Pattern */}
                    <div className="space-y-2">
                      <Label className={isDarkMode ? 'text-slate-200' : ''}>Fréquence</Label>
                      <Select value={recurringPattern} onValueChange={setRecurringPattern}>
                        <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Quotidien</SelectItem>
                          <SelectItem value="weekly">Hebdomadaire</SelectItem>
                          <SelectItem value="monthly">Mensuel</SelectItem>
                          <SelectItem value="yearly">Annuel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Interval */}
                    <div className="flex items-center gap-3">
                      <Label className={isDarkMode ? 'text-slate-200' : ''}>Tous les</Label>
                      <Input
                        type="number"
                        min="1"
                        value={recurringInterval}
                        onChange={(e) => setRecurringInterval(parseInt(e.target.value) || 1)}
                        className={`w-20 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                      />
                      <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                        {recurringPattern === 'daily' ? 'jour(s)' :
                         recurringPattern === 'weekly' ? 'semaine(s)' :
                         recurringPattern === 'monthly' ? 'mois' : 'an(s)'}
                      </span>
                    </div>

                    {/* Weekly days */}
                    {recurringPattern === 'weekly' && (
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-200' : ''}>Jours</Label>
                        <div className="flex flex-wrap gap-2">
                          {DAYS_OF_WEEK.map((day) => (
                            <Button
                              key={day.value}
                              type="button"
                              variant={recurringDays.includes(day.value) ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => toggleDay(day.value)}
                              className={recurringDays.includes(day.value) ? 'bg-indigo-600' : ''}
                            >
                              {day.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Start */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-200' : ''}>Début</Label>
                        <Input
                          type="date"
                          value={recurringStartDate}
                          onChange={(e) => setRecurringStartDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-200' : ''}>Heure</Label>
                        <Input
                          type="time"
                          value={recurringStartTime}
                          onChange={(e) => setRecurringStartTime(e.target.value)}
                          className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                        />
                      </div>
                    </div>

                    {/* End */}
                    <div className="space-y-3">
                      <Label className={isDarkMode ? 'text-slate-200' : ''}>Fin de la récurrence</Label>
                      <RadioGroup value={recurringEndType} onValueChange={setRecurringEndType} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="never" id="never" />
                          <Label htmlFor="never" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Sans limite
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="until" id="until" />
                          <Label htmlFor="until" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Jusqu'au
                          </Label>
                          {recurringEndType === 'until' && (
                            <Input
                              type="date"
                              value={recurringEndDate}
                              onChange={(e) => setRecurringEndDate(e.target.value)}
                              min={recurringStartDate}
                              className={`w-40 ml-2 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                            />
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="count" id="count" />
                          <Label htmlFor="count" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Après
                          </Label>
                          {recurringEndType === 'count' && (
                            <>
                              <Input
                                type="number"
                                min="1"
                                value={recurringOccurrences}
                                onChange={(e) => setRecurringOccurrences(parseInt(e.target.value) || 1)}
                                className={`w-20 ml-2 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                              />
                              <span className={isDarkMode ? 'text-slate-300' : ''}>occurrences</span>
                            </>
                          )}
                        </div>
                      </RadioGroup>
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Custom message from SUPADMIN config */}
                {smsConfig.custom_message && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                    <div className="flex gap-3">
                      <Info className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <p className={`text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-700'}`}>
                        {smsConfig.custom_message}
                      </p>
                    </div>
                  </div>
                )}

                {/* === TEST SMS Section === */}
                <div className={`p-4 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-[hsl(220,14%,25%)] bg-[hsl(220,14%,12%)]' : 'border-slate-200 bg-slate-50'}`}>
                  <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    <Phone className="h-4 w-4" />
                    Test SMS
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                        Numéros de test (un par ligne)
                      </Label>
                      <Textarea
                        value={testNumbers}
                        onChange={(e) => { setTestNumbers(e.target.value); setTestValidated(false); }}
                        placeholder="0612345678&#10;0698765432"
                        rows={3}
                        className={`font-mono text-sm ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                      />
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Button 
                        onClick={handleSendTest}
                        disabled={testSending || !message || !testNumbers.trim()}
                        variant="outline"
                      >
                        {testSending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Envoyer le test
                      </Button>
                      
                      {testResults.length > 0 && !testValidated && (
                        <Button onClick={handleValidateTest} className="bg-emerald-600 hover:bg-emerald-700">
                          <Check className="h-4 w-4 mr-2" />
                          Valider le test
                        </Button>
                      )}
                      
                      {testValidated && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Test validé
                        </Badge>
                      )}
                    </div>

                    {/* Test Results */}
                    {testResults.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {testResults.map((result, idx) => (
                          <div key={idx} className={`flex items-center gap-2 text-sm ${
                            result.status === 'success' 
                              ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                              : isDarkMode ? 'text-red-400' : 'text-red-600'
                          }`}>
                            {result.status === 'success' ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              <AlertTriangle className="h-4 w-4" />
                            )}
                            <span>{result.phone}</span>
                            {result.error && <span className="text-xs">({result.error})</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* === SEND BUTTON === */}
                <Button 
                  onClick={handleSendCampaign}
                  disabled={sending || !campaignName || !message || getRecipientsCount() === 0}
                  className="w-full h-14 text-lg font-semibold bg-indigo-600 hover:bg-indigo-700"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {sendMode === 'immediate' ? 'Envoi en cours...' : 'Programmation...'}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      {sendMode === 'immediate' ? 'Envoyer maintenant' : 
                       sendMode === 'scheduled' ? 'Programmer l\'envoi' : 
                       'Créer la récurrence'}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Compact info (removed summary, kept only preview which is now in step 2) */}
          <div className="space-y-6 lg:block hidden">
            {/* Mode indicator */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardContent className="p-4">
                <div className={`text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  <p className="text-sm">Mode d'envoi sélectionné</p>
                  <Badge variant="outline" className="mt-2 text-lg px-4 py-1">
                    {sendMode === 'immediate' ? '⚡ Immédiat' : 
                     sendMode === 'scheduled' ? '📅 Différé' : '🔄 Récurrent'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </SMSLayout>
  );
};

export default SMSMailingNew;
